"use client"

import { validateSolanaRecipientAddress } from "@/app/gacha/portfolio/gacha-transfer-address"
import { Trans } from "@lingui/react/macro"
import { usePrivy } from "@privy-io/react-auth"
import {
  type ConnectedStandardSolanaWallet,
  useSignTransaction,
  useWallets,
} from "@privy-io/react-auth/solana"
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  cn,
  sonnerToast,
} from "@rarible_int/anaconda_ui_components"
import { useCallback, useEffect, useMemo, useState } from "react"

import { postGachaJson } from "./gacha-api"
import {
  gachaCtaButtonRowPrimaryClassName,
  gachaCtaButtonRowSecondaryAutoClassName,
  gachaCtaButtonRowSecondaryClassName,
  gachaDialogFooterClassName,
} from "./gacha-cta-button-styles"
import { type GachaMachineView, formatUsd } from "./gacha-data"
import { normalizeGiftRecipientAddress } from "./gacha-gift-address"
import { showGachaGiftSentToast } from "./gacha-gift-sent-toast"
import { requestGachaPortfolioRefresh } from "./gacha-portfolio-refresh"
import { GachaProgressLabel } from "./gacha-progress-label"
import { getGachaDisclaimerGateAction } from "./gacha-purchase-dialog"
import { GachaPurchaseDisclaimerDialog } from "./gacha-purchase-disclaimer-dialog"
import { GachaSendGiftConfirmationBody } from "./gacha-send-gift-confirmation-dialog"
import { getStoredGachaTracking } from "./gacha-tracking"
import { useGachaApiOptions } from "./use-gacha-api-options"
import { useGachaDisclaimerAcceptance } from "./use-gacha-disclaimer-acceptance"
import { useGachaSolanaConfig } from "./use-gacha-solana-config"
import {
  requestGachaUsdcBalanceRefresh,
  useGachaUsdcBalance,
} from "./use-gacha-usdc-balance"

type GachaSendGiftButtonProps = {
  selectedMachine: GachaMachineView | null
  packImage: string
  className?: string
  disabled?: boolean
  initialQuantity?: number
  onGiftSent?: () => void
}

type GiftDialogStep = "form" | "confirm"

type GenerateGiftResponse = {
  transaction?: string
  serializedTransaction?: string
}

type SubmitTransactionResponse = {
  success?: boolean
  signature?: string
}

type GiftPackTransactionContext = {
  type: "GIFT_PACK"
  privyId: string
  walletAddress: string
  giftRecipientAddress: string
  giftCount?: number
  campaignId?: string | null
  referralCode?: string | null
  machineId: string
  machinePrice?: number | null
}

export function GachaSendGiftButton({
  selectedMachine,
  packImage,
  className,
  disabled = false,
  initialQuantity = 1,
  onGiftSent,
}: GachaSendGiftButtonProps) {
  const [open, setOpen] = useState(false)
  const [disclaimerOpen, setDisclaimerOpen] = useState(false)
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false)
  const [disclaimerError, setDisclaimerError] = useState<string | null>(null)
  const { ready, authenticated, login, user } = usePrivy()
  const { ready: walletsReady, wallets } = useWallets()
  const selectedWallet = useMemo(() => getPreferredWallet(wallets), [wallets])
  const {
    status: disclaimerAcceptanceStatus,
    ensureAcceptanceChecked,
    acceptDisclaimer,
    isAccepting: isDisclaimerAccepting,
  } = useGachaDisclaimerAcceptance()

  const openSendGiftDialog = useCallback(() => {
    setDisclaimerOpen(false)
    setDisclaimerAgreed(false)
    setDisclaimerError(null)
    setOpen(true)
  }, [])

  const requestSendGiftDialog = useCallback(async () => {
    if (!selectedMachine || isDisclaimerAccepting) return

    if (!ready || !walletsReady) {
      sonnerToast.error("Wallets are still loading")
      return
    }

    if (!authenticated) {
      login()
      return
    }

    if (!selectedWallet) {
      sonnerToast.error("Connect or create a Solana wallet to send a gift.")
      return
    }

    if (!user?.id) {
      sonnerToast.error("User session is still loading. Please try again.")
      return
    }

    setDisclaimerError(null)

    let gateAction = getGachaDisclaimerGateAction({
      cachedStatus: disclaimerAcceptanceStatus,
    })

    if (gateAction.action === "proceed") {
      openSendGiftDialog()
      return
    }

    try {
      const acceptance = await ensureAcceptanceChecked()
      gateAction = getGachaDisclaimerGateAction({
        cachedStatus: disclaimerAcceptanceStatus,
        checkedAccepted: acceptance.accepted,
      })
    } catch (caught) {
      gateAction = getGachaDisclaimerGateAction({
        cachedStatus: disclaimerAcceptanceStatus,
        checkErrorMessage: getGiftSendErrorMessage(caught),
      })
    }

    if (gateAction.action === "proceed") {
      openSendGiftDialog()
      return
    }

    setDisclaimerError(gateAction.errorMessage ?? null)
    setDisclaimerAgreed(false)
    setDisclaimerOpen(true)
  }, [
    authenticated,
    disclaimerAcceptanceStatus,
    ensureAcceptanceChecked,
    isDisclaimerAccepting,
    login,
    openSendGiftDialog,
    ready,
    selectedMachine,
    selectedWallet,
    user?.id,
    walletsReady,
  ])

  const handleDisclaimerOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isDisclaimerAccepting && !nextOpen) return

      setDisclaimerOpen(nextOpen)
      if (nextOpen) return

      setDisclaimerAgreed(false)
      setDisclaimerError(null)
    },
    [isDisclaimerAccepting],
  )

  const handleDisclaimerConfirm = useCallback(async () => {
    if (!disclaimerAgreed || isDisclaimerAccepting) return

    if (!selectedWallet) {
      setDisclaimerError("Connect or create a Solana wallet to send a gift.")
      return
    }

    try {
      setDisclaimerError(null)
      await acceptDisclaimer(selectedWallet.address)
      openSendGiftDialog()
    } catch (caught) {
      setDisclaimerError(getGiftSendErrorMessage(caught))
    }
  }, [
    acceptDisclaimer,
    disclaimerAgreed,
    isDisclaimerAccepting,
    openSendGiftDialog,
    selectedWallet,
  ])

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={cn(gachaCtaButtonRowSecondaryAutoClassName, className)}
        disabled={
          disabled ||
          !selectedMachine ||
          selectedMachine.isSoldOut ||
          isDisclaimerAccepting
        }
        data-testid="gacha-send-gift-button"
        onClick={requestSendGiftDialog}
      >
        <Trans>Send as Gift</Trans>
      </Button>
      <GachaSendGiftDialog
        selectedMachine={selectedMachine}
        packImage={packImage}
        open={open}
        onOpenChange={setOpen}
        initialQuantity={initialQuantity}
        onGiftSent={onGiftSent}
      />
      <GachaPurchaseDisclaimerDialog
        open={disclaimerOpen}
        onOpenChange={handleDisclaimerOpenChange}
        title="Before you send gift"
        agreed={disclaimerAgreed}
        onAgreedChange={setDisclaimerAgreed}
        onConfirm={handleDisclaimerConfirm}
        isSubmitting={isDisclaimerAccepting}
        errorMessage={disclaimerError}
      />
    </>
  )
}

function GachaSendGiftDialog({
  selectedMachine,
  packImage,
  open,
  onOpenChange,
  initialQuantity = 1,
  onGiftSent,
}: {
  selectedMachine: GachaMachineView | null
  packImage: string
  open: boolean
  onOpenChange: (open: boolean) => void
  initialQuantity?: number
  onGiftSent?: () => void
}) {
  const { bffBaseUrl: gachaBffBaseUrl } = useGachaApiOptions()
  const { chain: gachaSolanaChain } = useGachaSolanaConfig()
  const { ready, authenticated, login, user } = usePrivy()
  const { ready: walletsReady, wallets } = useWallets()
  const { signTransaction } = useSignTransaction()
  const [step, setStep] = useState<GiftDialogStep>("form")
  const [recipientAddress, setRecipientAddress] = useState("")
  const [quantity, setQuantity] = useState(initialQuantity)
  const [busy, setBusy] = useState(false)
  const [busyLabel, setBusyLabel] = useState("")
  const [progressDots, setProgressDots] = useState(".")
  const [disclaimerOpen, setDisclaimerOpen] = useState(false)
  const [disclaimerAgreed, setDisclaimerAgreed] = useState(false)
  const [disclaimerError, setDisclaimerError] = useState<string | null>(null)
  const selectedWallet = useMemo(() => getPreferredWallet(wallets), [wallets])
  const {
    status: disclaimerAcceptanceStatus,
    ensureAcceptanceChecked,
    acceptDisclaimer,
    isAccepting: isDisclaimerAccepting,
  } = useGachaDisclaimerAcceptance()
  const { balanceAmount, isLoading: isBalanceLoading } = useGachaUsdcBalance(
    selectedWallet?.address,
  )
  const quantityLimit = selectedMachine
    ? Math.max(1, Math.min(10, selectedMachine.stockTotal || 10))
    : 10
  const normalizedRecipientAddress =
    normalizeGiftRecipientAddress(recipientAddress)
  const canContinue =
    recipientAddress.trim().length > 0 && Boolean(selectedMachine)
  const totalPriceAmount =
    selectedMachine && Number.isFinite(selectedMachine.price)
      ? selectedMachine.price * quantity
      : 0
  const totalPrice = formatUsd(totalPriceAmount)
  const packName = selectedMachine?.name ?? "Gacha Pack"

  useEffect(() => {
    if (!open) {
      setStep("form")
      setBusy(false)
      setBusyLabel("")
      return
    }

    setRecipientAddress("")
    setQuantity(Math.min(quantityLimit, Math.max(1, initialQuantity)))
    setStep("form")
  }, [initialQuantity, open, quantityLimit])

  useEffect(() => {
    if (!busy) {
      setProgressDots(".")
      return
    }

    const id = window.setInterval(() => {
      setProgressDots((dots) => (dots.length >= 3 ? "." : `${dots}.`))
    }, 400)

    return () => window.clearInterval(id)
  }, [busy])

  const giftSummary = useMemo(
    () => ({
      packName,
      packImage,
      recipientAddress: normalizedRecipientAddress,
      quantity,
      totalPrice,
    }),
    [normalizedRecipientAddress, packImage, packName, quantity, totalPrice],
  )

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (busy && !nextOpen) return
      if (!nextOpen) {
        setStep("form")
        setDisclaimerOpen(false)
        setDisclaimerAgreed(false)
        setDisclaimerError(null)
      }
      onOpenChange(nextOpen)
    },
    [busy, onOpenChange],
  )

  const handleContinue = () => {
    if (!selectedMachine || !canContinue) return

    try {
      validateSolanaRecipientAddress(normalizedRecipientAddress)
      setStep("confirm")
    } catch {
      sonnerToast.error("Invalid Solana address.")
    }
  }

  const signAndSubmitGiftTransaction = useCallback(
    async (
      wallet: ConnectedStandardSolanaWallet,
      transactionBase64: string,
      gachaTransaction: GiftPackTransactionContext,
    ) => {
      const { signedTransaction } = await signTransaction({
        transaction: base64ToBytes(transactionBase64),
        wallet,
        chain: gachaSolanaChain,
      })

      return postGachaJson<SubmitTransactionResponse>(
        "/submitTransaction",
        {
          signedTransaction: bytesToBase64(signedTransaction),
          gachaTransaction,
        },
        { bffBaseUrl: gachaBffBaseUrl },
      )
    },
    [gachaBffBaseUrl, gachaSolanaChain, signTransaction],
  )

  const handleConfirmSend = useCallback(async () => {
    if (!selectedMachine) return

    if (!ready || !walletsReady) {
      sonnerToast.error("Wallets are still loading")
      return
    }

    if (!authenticated) {
      login()
      return
    }

    if (!selectedWallet) {
      sonnerToast.error("Connect or create a Solana wallet to send a gift.")
      return
    }

    const privyId = user?.id
    if (!privyId) {
      sonnerToast.error("User session is still loading. Please try again.")
      return
    }

    try {
      validateSolanaRecipientAddress(normalizedRecipientAddress)
    } catch {
      sonnerToast.error("Invalid Solana address.")
      return
    }

    if (selectedMachine.stockTotal <= 0) {
      sonnerToast.error("This pack is sold out. Please choose another pack.")
      return
    }

    if (quantity > selectedMachine.stockTotal) {
      sonnerToast.error(
        `Only ${selectedMachine.stockTotal} ${selectedMachine.stockTotal === 1 ? "pack is" : "packs are"} left. Please try again with ${selectedMachine.stockTotal}.`,
      )
      return
    }

    if (isBalanceLoading) {
      sonnerToast.error("Checking USDC balance. Please try again in a moment.")
      return
    }

    if (balanceAmount !== null && balanceAmount + 0.000001 < totalPriceAmount) {
      sonnerToast.error(
        `Insufficient USDC. You need ${formatUsd(totalPriceAmount)}, but this wallet has ${formatUsd(balanceAmount)} USDC.`,
      )
      return
    }

    try {
      setBusy(true)
      setBusyLabel("Creating gift")
      const tracking = getStoredGachaTracking()
      const gift = await postGachaJson<GenerateGiftResponse>(
        "/generateGift",
        {
          sender: selectedWallet.address,
          receiver: normalizedRecipientAddress,
          packType: selectedMachine.code,
          count: quantity,
        },
        { bffBaseUrl: gachaBffBaseUrl },
      )
      const transaction = gift.transaction ?? gift.serializedTransaction
      if (!transaction) {
        throw new Error("Gift transaction was not returned.")
      }

      setBusyLabel("Sign in wallet")
      await signAndSubmitGiftTransaction(selectedWallet, transaction, {
        type: "GIFT_PACK",
        privyId,
        walletAddress: selectedWallet.address,
        giftRecipientAddress: normalizedRecipientAddress,
        giftCount: quantity,
        campaignId: tracking.campaignId,
        referralCode: tracking.referralCode,
        machineId: selectedMachine.code,
        machinePrice: selectedMachine.price,
      })

      requestGachaUsdcBalanceRefresh(selectedWallet.address, -totalPriceAmount)
      requestGachaPortfolioRefresh()
      showGachaGiftSentToast({
        packImage,
        quantity,
        recipientAddress: normalizedRecipientAddress,
      })
      onGiftSent?.()
      handleOpenChange(false)
    } catch (caught) {
      sonnerToast.error(getGiftSendErrorMessage(caught))
    } finally {
      setBusy(false)
      setBusyLabel("")
    }
  }, [
    authenticated,
    balanceAmount,
    gachaBffBaseUrl,
    handleOpenChange,
    isBalanceLoading,
    login,
    normalizedRecipientAddress,
    onGiftSent,
    packImage,
    quantity,
    ready,
    selectedMachine,
    selectedWallet,
    signAndSubmitGiftTransaction,
    totalPriceAmount,
    user?.id,
    walletsReady,
  ])

  const requestSendGiftConfirmation = useCallback(async () => {
    if (busy || isDisclaimerAccepting) return

    if (!selectedMachine) return

    if (!ready || !walletsReady) {
      sonnerToast.error("Wallets are still loading")
      return
    }

    if (!authenticated) {
      login()
      return
    }

    if (!selectedWallet) {
      sonnerToast.error("Connect or create a Solana wallet to send a gift.")
      return
    }

    if (!user?.id) {
      sonnerToast.error("User session is still loading. Please try again.")
      return
    }

    setDisclaimerError(null)

    let gateAction = getGachaDisclaimerGateAction({
      cachedStatus: disclaimerAcceptanceStatus,
    })

    if (gateAction.action === "proceed") {
      void handleConfirmSend()
      return
    }

    try {
      const acceptance = await ensureAcceptanceChecked()
      gateAction = getGachaDisclaimerGateAction({
        cachedStatus: disclaimerAcceptanceStatus,
        checkedAccepted: acceptance.accepted,
      })
    } catch (caught) {
      gateAction = getGachaDisclaimerGateAction({
        cachedStatus: disclaimerAcceptanceStatus,
        checkErrorMessage: getGiftSendErrorMessage(caught),
      })
    }

    if (gateAction.action === "proceed") {
      void handleConfirmSend()
      return
    }

    setDisclaimerError(gateAction.errorMessage ?? null)
    setDisclaimerAgreed(false)
    setDisclaimerOpen(true)
  }, [
    authenticated,
    busy,
    disclaimerAcceptanceStatus,
    ensureAcceptanceChecked,
    handleConfirmSend,
    isDisclaimerAccepting,
    login,
    ready,
    selectedMachine,
    selectedWallet,
    user?.id,
    walletsReady,
  ])

  const handleDisclaimerOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isDisclaimerAccepting && !nextOpen) return

      setDisclaimerOpen(nextOpen)
      if (nextOpen) return

      setDisclaimerAgreed(false)
      setDisclaimerError(null)
    },
    [isDisclaimerAccepting],
  )

  const handleDisclaimerConfirm = useCallback(async () => {
    if (!disclaimerAgreed || isDisclaimerAccepting) return

    if (!selectedWallet) {
      setDisclaimerError("Connect or create a Solana wallet to send a gift.")
      return
    }

    try {
      setDisclaimerError(null)
      await acceptDisclaimer(selectedWallet.address)
      setDisclaimerOpen(false)
      setDisclaimerAgreed(false)
      void handleConfirmSend()
    } catch (caught) {
      setDisclaimerError(getGiftSendErrorMessage(caught))
    }
  }, [
    acceptDisclaimer,
    disclaimerAgreed,
    handleConfirmSend,
    isDisclaimerAccepting,
    selectedWallet,
  ])

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="flex max-h-[min(88dvh,760px)] max-w-[480px] flex-col gap-0 overflow-hidden p-0"
          data-testid={
            step === "form"
              ? "gacha-send-gift-dialog"
              : "gacha-send-gift-confirmation-dialog"
          }
        >
          {step === "form" ? (
            <>
              <DialogHeader className="shrink-0 gap-2 space-y-0 p-5 pb-0 text-left">
                <DialogTitle className="font-headline font-normal text-2xl leading-tight tracking-tight text-foreground">
                  <Trans>Gift Packs</Trans>
                </DialogTitle>
              </DialogHeader>

              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
                  <div className="flex flex-col gap-5">
                    <div className="flex items-center gap-3 rounded-lg border border-foreground/10 bg-foreground/[0.02] p-3">
                      <div className="relative aspect-square w-16 shrink-0 overflow-hidden rounded-md bg-foreground/[0.04]">
                        <img
                          src={packImage}
                          alt=""
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <p className="min-w-0 text-sm text-foreground">
                        {packName}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Label htmlFor="gacha-gift-recipient-address">
                        <Trans>Recipient Address</Trans>
                      </Label>
                      <Input
                        id="gacha-gift-recipient-address"
                        value={recipientAddress}
                        onChange={(event) =>
                          setRecipientAddress(event.target.value)
                        }
                        placeholder="Solana address"
                        className="h-11 border-foreground/10 font-mono"
                        data-testid="gacha-send-gift-recipient-input"
                      />
                      <p className="text-xs leading-relaxed text-foreground/60">
                        <Trans>
                          Only Solana wallets are currently supported.
                        </Trans>
                        <br />
                        <Trans>
                          Please verify the recipient address carefully. Gift
                          transfers cannot be reversed.
                        </Trans>
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-foreground/60">
                        <Trans>Quantity</Trans>
                      </span>
                      <div className="flex h-10 items-center overflow-hidden rounded-md border border-foreground/10">
                        <button
                          type="button"
                          className="flex h-full w-10 items-center justify-center text-lg text-foreground transition-colors hover:bg-foreground/10 disabled:cursor-not-allowed disabled:opacity-40"
                          onClick={() =>
                            setQuantity((value) => Math.max(1, value - 1))
                          }
                          disabled={quantity <= 1 || busy}
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <div
                          className="min-w-10 text-center font-mono text-sm"
                          data-testid="gacha-send-gift-quantity"
                        >
                          {quantity}
                        </div>
                        <button
                          type="button"
                          className="flex h-full w-10 items-center justify-center text-lg text-foreground transition-colors hover:bg-foreground/10 disabled:cursor-not-allowed disabled:opacity-40"
                          onClick={() =>
                            setQuantity((value) =>
                              Math.min(quantityLimit, value + 1),
                            )
                          }
                          disabled={quantity >= quantityLimit || busy}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 flex shrink-0 flex-col gap-4 border-foreground/10 border-t bg-background p-5">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-foreground/60">
                      <Trans>Total</Trans>
                    </span>
                    <span
                      className="font-mono text-foreground"
                      data-testid="gacha-send-gift-total"
                    >
                      {totalPrice}
                    </span>
                  </div>

                  <DialogFooter className={gachaDialogFooterClassName}>
                    <Button
                      type="button"
                      variant="outline"
                      className={gachaCtaButtonRowSecondaryClassName}
                      onClick={() => handleOpenChange(false)}
                      disabled={busy}
                    >
                      <Trans>Cancel</Trans>
                    </Button>
                    <Button
                      type="button"
                      variant="default"
                      className={gachaCtaButtonRowPrimaryClassName}
                      disabled={!canContinue || busy}
                      data-testid="gacha-send-gift-continue"
                      onClick={handleContinue}
                    >
                      <Trans>Continue</Trans>
                    </Button>
                  </DialogFooter>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-5 p-5">
              <GachaSendGiftConfirmationBody
                summary={giftSummary}
                busy={busy || isDisclaimerAccepting}
                onBack={() => setStep("form")}
                onConfirm={requestSendGiftConfirmation}
              />
              {busy ? (
                <div className="sr-only" aria-live="polite">
                  <GachaProgressLabel label={busyLabel} dots={progressDots} />
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <GachaPurchaseDisclaimerDialog
        open={disclaimerOpen}
        onOpenChange={handleDisclaimerOpenChange}
        title="Before you send gift"
        agreed={disclaimerAgreed}
        onAgreedChange={setDisclaimerAgreed}
        onConfirm={handleDisclaimerConfirm}
        isSubmitting={isDisclaimerAccepting}
        errorMessage={disclaimerError}
      />
    </>
  )
}

function getPreferredWallet(wallets: ConnectedStandardSolanaWallet[]) {
  const browserWallet = wallets.find(
    (wallet) =>
      (wallet as { connectorType?: string }).connectorType === "solana_adapter",
  )
  if (browserWallet) return browserWallet

  const nonPrivyWallet = wallets.find(
    (wallet) => wallet.standardWallet.name !== "Privy",
  )
  if (nonPrivyWallet) return nonPrivyWallet

  return (
    wallets.find((wallet) => wallet.standardWallet.name === "Privy") ??
    wallets[0] ??
    null
  )
}

function getGiftSendErrorMessage(caught: unknown) {
  if (!(caught instanceof Error)) {
    return "Gift could not be sent. Please try again."
  }

  const message = caught.message.toLowerCase()
  if (message.includes("reject") || message.includes("cancel")) {
    return "Gift sending cancelled."
  }

  return caught.message || "Gift could not be sent. Please try again."
}

function base64ToBytes(value: string) {
  const binary =
    typeof window === "undefined"
      ? Buffer.from(value, "base64").toString("binary")
      : window.atob(value)

  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

function bytesToBase64(bytes: Uint8Array) {
  if (typeof window === "undefined") {
    return Buffer.from(bytes).toString("base64")
  }

  let binary = ""
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return window.btoa(binary)
}
